<template>
	<article
		class="info-card gap-300-lg gap-150-md display-flex-md align-items-center-md justify-content-center-md"
		:class="{ 'text-color-white': theme === 'dark' }"
	>
		<div class="col-6-md px-400-xl mb-150-smo">
			<div v-if="card.heading" class="overline mb-100" :class="headingTextColor">
				{{ card.heading }}
			</div>
			<h3 :class="[cardHeadingStyle, { 'text-color-white': theme === 'dark' }]">
				{{ card.title }}
			</h3>
			<p v-for="(desc, descId) in card.description" :key="descId" v-html="desc"></p>
			<p v-if="card.link && !useButtonLink" class="mt-100">
				<BaseCtaLink :link="card.link" :is-orange="theme === 'dark'" :icon="linkIcon" />
			</p>
			<div v-if="card.link && useButtonLink" class="mt-200 delta">
				<BaseLink :link="card.link" :classes="'button button-primary'" />
			</div>

			<p v-for="note in card.footnotes" :key="note" :class="'footnote eta muted mt-300'" v-html="note"></p>
		</div>
		<div role="figure" :class="['col-5-md pl-100-smo pr-100-smo', { 'order--1-md': positionTextRight }]">
			<picture v-if="card.fallbackImageUrl">
				<source :srcset="imgSrc" type="image/avif" />
				<img v-reveal:fade-up :src="fallbackImgSrc" :alt="card.imageAlt" />
			</picture>
			<img v-else v-reveal:fade-up :src="imgSrc" :alt="card.imageAlt" />
		</div>
	</article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { appCloudfrontPath } from '@constants';
import { InfoCard } from 'types/layout';
interface Props {
	card: InfoCard;
	cardHeadingStyle?: string;
	useButtonLink?: boolean;
	useFullImgUrl?: boolean;
	positionTextRight?: boolean;
	theme?: 'light' | 'dark' | 'greige';
}
const props = withDefaults(defineProps<Props>(), {
	cardHeadingStyle: 'heading-1',
	useButtonLink: false,
	useFullImgUrl: false,
	positionTextRight: true,
	theme: 'light'
});

const imgSrc = computed((): string => {
	return props.useFullImgUrl ? props.card.imageUrl : appCloudfrontPath + props.card.imageUrl;
});

const fallbackImgSrc = computed((): string => {
	if (!props.card.fallbackImageUrl) return '';
	return props.useFullImgUrl ? props.card.fallbackImageUrl : appCloudfrontPath + props.card.fallbackImageUrl;
});

const headingTextColor = computed((): string => {
	switch (props.theme) {
		case 'dark':
			return 'text-color-white';
		case 'greige':
			return 'text-color-content-secondary';
		case 'light':
		default:
			return 'text-color-current-color';
	}
});

const linkIcon = computed((): string => {
	if (!props.card.link) return '';
	if (props.card.link.target === '_blank') return 'external-link';
	return 'arrow';
});
</script>
